import React, { useState } from "react";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import useApiResource from "../../../useApiResource";
import { FormSection } from "../../FormBits";
import ConfirmModal from "../../ConfirmModal";
import { formatWhen, isBlank, newestFirst, NoteEditor, NoteModal } from "./noteBits";

/*
Internal (staff-only) notes - the warranty screen's "Communications" tab, which
is hidden from Contractor and Commercial roles.
Same endpoints and fields as Warranties/SingleWarranty/CommercialNotes:
GET  ticket/{id}/commercial-note/  -> [{ id, email, created, note (HTML), child[] }]
PUT  ticket/{id}/commercial-note/  multipart `note` (+ `parent` for a reply)
DELETE commercial-note/{noteId}
*/
const CommercialNoteCard = function (props) {
  const { note, isReply, onReply, onDelete } = props;
  return (
    <div className={"tn-note" + (isReply ? " tn-note-reply" : "")}>
      <div className="tn-note-head">
        <span className="tn-note-author">{note.email || "—"}</span>
        <span className="tn-note-when tn-mono">{formatWhen(note.created)}</span>
      </div>
      <div className="tn-note-body" dangerouslySetInnerHTML={{ __html: note.note }} />
      <div className="tn-note-actions">
        {onReply ? (
          <button type="button" className="tn-btn tn-btn-ghost tn-btn-sm" onClick={onReply}>
            Reply
          </button>
        ) : null}
        <button type="button" className="tn-btn tn-btn-ghost tn-btn-sm" onClick={onDelete}>
          Delete
        </button>
      </div>
    </div>
  );
};

const CommercialNotesSection = function (props) {
  const { ticket, protectedFetch } = props;
  const ticketId = ticket && ticket.id;

  const notes = useApiResource(
    protectedFetch,
    ticketId ? apiEndpoints.ticket.commercialNotes(ticketId) : null,
    null,
    { skip: !ticketId }
  );

  // null when closed; { parentId } when the editor is open (parentId null = new note).
  const [editor, setEditor] = useState(null);
  const [draft, setDraft] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const openEditor = function (parentId) {
    setDraft("");
    setError(null);
    setEditor({ parentId: parentId || null });
  };

  const closeEditor = function () {
    if (busy) return;
    setEditor(null);
    setDraft("");
  };

  const saveNote = function () {
    if (isBlank(draft) || busy) return;
    const formData = new FormData();
    if (editor.parentId) formData.append("parent", editor.parentId);
    formData.append("note", draft);

    setBusy(true);
    setError(null);
    protectedFetch(
      apiEndpoints.ticket.commercialNotes(ticketId),
      "PUT",
      formData,
      errorFunctions.checkResponseSent,
      true
    )
      .then(function (ok) {
        setBusy(false);
        if (!ok) {
          setError("Couldn't save the note. Please try again.");
          return;
        }
        setEditor(null);
        setDraft("");
        notes.reload();
      })
      .catch(function () {
        setBusy(false);
        setError("Couldn't save the note. Please try again.");
      });
  };

  const confirmDelete = function () {
    setBusy(true);
    setError(null);
    protectedFetch(
      apiEndpoints.commercialNote.detail(deleteId),
      "DELETE",
      null,
      errorFunctions.checkResponseSent
    )
      .then(function (ok) {
        setBusy(false);
        setDeleteId(null);
        if (!ok) {
          setError("Couldn't delete the note. Please try again.");
          return;
        }
        notes.reload();
      })
      .catch(function () {
        setBusy(false);
        setDeleteId(null);
        setError("Couldn't delete the note. Please try again.");
      });
  };

  const list = newestFirst(notes.data);

  return (
    <React.Fragment>
      <FormSection
        title={
          <React.Fragment>
            Internal Notes <span className="tn-note-badge">Staff only</span>
          </React.Fragment>
        }
        note={
          <button
            type="button"
            className="tn-btn tn-btn-primary tn-btn-sm"
            onClick={function () {
              openEditor(null);
            }}
            disabled={!ticketId}
          >
            Add Note
          </button>
        }
      >
        {error && !editor ? (
          <div className="tn-alert" role="alert">
            <span className="tn-alert-text">{error}</span>
          </div>
        ) : null}

        {notes.error ? (
          <div className="tn-alert" role="alert">
            <span className="tn-alert-text">{notes.error}</span>
            <button type="button" className="tn-alert-retry" onClick={notes.reload}>
              Retry
            </button>
          </div>
        ) : null}

        {notes.loading && !notes.hasLoadedOnce ? (
          <div className="tn-empty">Loading notes…</div>
        ) : list.length ? (
          <div className="tn-notes">
            {list.map(function (note) {
              return (
                <div className="tn-note-thread" key={note.id || note.created}>
                  <CommercialNoteCard
                    note={note}
                    onReply={function () {
                      openEditor(note.id);
                    }}
                    onDelete={function () {
                      setDeleteId(note.id);
                    }}
                  />
                  {(note.child || []).map(function (reply) {
                    return (
                      <CommercialNoteCard
                        key={reply.id || reply.created}
                        note={reply}
                        isReply
                        onDelete={function () {
                          setDeleteId(reply.id);
                        }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="tn-empty">No internal notes yet.</div>
        )}
      </FormSection>

      {editor ? (
        <NoteModal
          title={editor.parentId ? "Reply to Internal Note" : "Add Internal Note"}
          busy={busy}
          error={error}
          saveDisabled={isBlank(draft)}
          onSave={saveNote}
          onClose={closeEditor}
        >
          <NoteEditor value={draft} onChange={setDraft} />
        </NoteModal>
      ) : null}

      <ConfirmModal
        open={!!deleteId}
        title="Delete note"
        message="Delete this note? This can't be undone."
        confirmLabel="Delete"
        busy={busy}
        onConfirm={confirmDelete}
        onCancel={function () {
          if (!busy) setDeleteId(null);
        }}
      />
    </React.Fragment>
  );
};

export default CommercialNotesSection;
